import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2, Clock, CalendarDays } from "lucide-react";
import { getPost, BLOG_POSTS } from "@/data/blogPosts";
import { LOGO } from "@/lib/constants";
import { Section, Overline, Reveal } from "@/components/Primitives";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";

const fmtDate = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

// Chaque article renvoie vers son guide "pilier" (page SEO) pour renforcer le maillage thématique.
const PILLAR = {
  "preparer-son-pvt-australie": { to: "/visa-australie", label: "Guide complet : Visa Australie" },
  "budget-partir-australie": { to: "/guide-australie", label: "Guide complet : Partir en Australie" },
  "trouver-job-australie": { to: "/travailler-en-australie", label: "Guide complet : Travailler en Australie" },
  "meilleures-villes-australie-expatries": { to: "/guide-australie", label: "Guide complet : Partir en Australie" },
  "visa-etudiant-australie": { to: "/visa-australie", label: "Guide complet : Visa Australie" },
  "entree-express-canada-guide": { to: "/immigration-canada", label: "Guide complet : Immigration Canada" },
  "pvt-canada-etapes": { to: "/pvt-canada", label: "Guide complet : PVT Canada" },
  "immigration-francophone-canada": { to: "/immigration-canada", label: "Guide complet : Immigration Canada" },
  "cout-de-la-vie-canada": { to: "/visa-canada", label: "Guide complet : Visa Canada" },
  "travailler-au-quebec": { to: "/immigration-canada", label: "Guide complet : Immigration Canada" },
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <Navigate to="/blog" replace />;

  const url = `https://worktravelexpert.net/blog/${slug}`;
  const related = (post.related || []).map(getPost).filter(Boolean);
  const pillar = PILLAR[slug];

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: "https://worktravelexpert.net/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: "https://worktravelexpert.net/blog" },
        { "@type": "ListItem", position: 3, name: post.h1, item: url },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.h1,
      description: post.metaDescription,
      image: post.image,
      datePublished: post.date,
      dateModified: post.date,
      inLanguage: "fr-FR",
      keywords: post.keywords,
      articleSection: post.tags,
      author: { "@type": "Organization", name: "Work & Travel Expert" },
      publisher: {
        "@type": "Organization",
        name: "Work & Travel Expert",
        logo: { "@type": "ImageObject", url: LOGO },
      },
      mainEntityOfPage: url,
    },
  ];

  return (
    <div data-testid={`blog-post-${slug}`}>
      <Seo
        title={post.title}
        description={post.metaDescription}
        keywords={post.keywords}
        path={`/blog/${slug}`}
        image={post.image}
        type="article"
        schema={schema}
      />

      {/* HERO */}
      <div className="relative bg-navy">
        <img src={post.image} alt={post.h1} loading="eager" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-navy/70" />
        <Section className="relative z-10 py-20 sm:py-24">
          <Breadcrumbs theme="dark" items={[{ name: "Blog", to: "/blog" }, { name: post.h1 }]} />
          <Link to="/blog" data-testid="blog-back-link" className="mt-5 inline-flex items-center gap-2 text-white/70 hover:text-gold transition-colors text-sm">
            <ArrowLeft size={16} /> Retour au blog
          </Link>
          <div className="mt-6 flex flex-wrap gap-2">
            {post.tags.map((tg) => (
              <span key={tg} className="bg-gold/15 border border-gold/40 text-gold text-xs font-semibold px-3 py-1">{tg}</span>
            ))}
          </div>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-medium text-white max-w-3xl leading-[1.1]">{post.h1}</h1>
          <div className="mt-5 flex items-center gap-5 text-sm text-white/70">
            <span className="inline-flex items-center gap-1.5"><CalendarDays size={15} /> {fmtDate(post.date)}</span>
            <span className="inline-flex items-center gap-1.5"><Clock size={15} /> {post.readTime} min de lecture</span>
          </div>
        </Section>
      </div>

      {/* CONTENU */}
      <Section className="py-16 max-w-3xl">
        <p className="text-lg text-slate-700 leading-relaxed">{post.intro}</p>
        {pillar && (
          <Link to={pillar.to} data-testid="blog-pillar-link" className="group mt-6 flex items-center justify-between gap-4 border-l-4 border-gold bg-[#F8F9FA] px-5 py-4 hover:bg-gold/5 transition-colors">
            <span className="text-navy font-medium">{pillar.label}</span>
            <ArrowRight size={18} className="text-gold shrink-0 group-hover:translate-x-1 transition-transform" />
          </Link>
        )}
        <article className="mt-12 space-y-14">
          {post.sections.map((s, i) => (
            <Reveal key={i} delay={i * 0.04}>
              <section data-testid={`blog-section-${i}`}>
                <h2 className="font-heading text-2xl sm:text-3xl text-navy">{s.h}</h2>
                <div className="gold-line mt-5" />
                <p className="mt-6 text-slate-700 leading-relaxed">{s.p}</p>
                {s.list && (
                  <ul className="mt-6 space-y-3">
                    {s.list.map((li, k) => (
                      <li key={k} className="flex items-start gap-3 text-slate-700">
                        <CheckCircle2 size={20} className="text-gold shrink-0 mt-0.5" />
                        <span>{li}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </Reveal>
          ))}
        </article>

        {/* CTA RÉSERVATION */}
        <div className="mt-16 bg-navy text-white p-8 sm:p-10" data-testid="blog-cta-box">
          <h2 className="font-heading text-2xl sm:text-3xl">Envie de passer à l'action ?</h2>
          <p className="mt-4 text-white/75 leading-relaxed">Nos conseillers évaluent votre éligibilité et préparent votre dossier de A à Z. Réservez votre accompagnement et avancez sereinement dans votre projet.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link to="/nos-packs" data-testid="blog-cta-packs" className="group inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover text-white px-8 py-3.5 font-medium transition-colors">
              Réserver mon accompagnement <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/contact" data-testid="blog-cta-contact" className="inline-flex items-center justify-center gap-2 border border-white/40 text-white px-8 py-3.5 font-medium hover:bg-white hover:text-navy transition-colors">
              Poser une question
            </Link>
          </div>
        </div>

        {/* ARTICLES LIÉS */}
        {related.length > 0 && (
          <div className="mt-16">
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">À lire également</p>
            <div className="mt-6 grid gap-6 sm:grid-cols-3">
              {related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`} data-testid={`blog-related-${r.slug}`} className="group block border border-border bg-white hover:border-gold transition-colors">
                  <div className="h-32 overflow-hidden">
                    <img src={r.image} alt={r.h1} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-5">
                    <span className="text-xs uppercase tracking-widest font-semibold text-gold">{r.tags[0]}</span>
                    <h3 className="font-heading text-base text-navy mt-2 leading-snug group-hover:text-gold transition-colors">{r.h1}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}
